/**
 * Suppression is an action (Constitution invariant 7): every suppression has
 * a reason, a policy or a person, a time, and a reversible trace. This module
 * is the one way to construct one, and it decides which of a person's
 * suppressions still apply to a run: a suppression is lifted by an appended
 * `unsuppress` correction (never by a delete), and one with duration
 * `until_new_evidence` stops applying when material new evidence arrives —
 * a claim or a transition in the same lineage group whose evidence is dated
 * after the suppression. A rerun over the same evidence never resurfaces a
 * suppressed item.
 */
import { suppressionId } from '@cordelia/core'
import type {
  Claim,
  ClaimId,
  Correction,
  Evidence,
  EvidenceId,
  LedgerItemId,
  LineageGroup,
  SourceId,
  Suppression,
  SuppressionId,
} from '@cordelia/core'

export type SuppressionActor = Suppression['actor']
export type SuppressionScope = Suppression['scope']
export type SuppressionDuration = Suppression['duration']

/** What a person can say when they suppress a card (M4.1's plain choices that are suppressions). */
export const USER_SUPPRESSION_REASONS = [
  'user:dismiss',
  'user:irrelevant_or_private',
  'user:not_a_commitment',
  'user:draft_abandoned',
] as const
export type UserSuppressionReason = (typeof USER_SUPPRESSION_REASONS)[number]

export type SuppressionInput = {
  readonly item_id?: LedgerItemId | null
  readonly claim_id?: ClaimId | null
  readonly source_id?: SourceId | null
  readonly reason: string
  readonly actor: SuppressionActor
  readonly time: string
  readonly scope?: SuppressionScope
  readonly duration?: SuppressionDuration
}

/** The only constructor. A suppression without a reason or a target cannot exist. */
export function makeSuppression(input: SuppressionInput): Suppression {
  const reason = input.reason.trim()
  if (reason.length === 0) throw new Error('a suppression needs a reason')
  const target = input.item_id ?? input.claim_id ?? input.source_id ?? null
  if (target === null) throw new Error('a suppression needs a target: an item, a claim or a source')
  return {
    id: suppressionId(target, reason, input.time),
    item_id: input.item_id ?? null,
    claim_id: input.claim_id ?? null,
    source_id: input.source_id ?? null,
    reason,
    actor: input.actor,
    time: input.time,
    reversible: true,
    scope: input.scope ?? 'item',
    duration: input.duration ?? 'until_new_evidence',
  }
}

/** A person's suppression of a card: the item and its head claim, a reason from the closed set. */
export type UserSuppression = {
  readonly item_id: LedgerItemId
  readonly claim_id: ClaimId
  readonly reason: UserSuppressionReason
  readonly time: string
  readonly scope?: SuppressionScope
  readonly duration?: SuppressionDuration
}

export const userSuppression = (input: UserSuppression): Suppression =>
  makeSuppression({ ...input, actor: 'user' })

export type SuppressionState = 'active' | 'lifted' | 'reactivated'

export type UserSuppressionResult = {
  /** Every user suppression, as a record for the ledger — lifted and reactivated ones included. */
  readonly records: readonly Suppression[]
  /** The ones routing applies this run. */
  readonly active: readonly Suppression[]
  readonly states: ReadonlyMap<SuppressionId, SuppressionState>
  /** For a reactivated suppression, the evidence that reactivated it. */
  readonly reactivated_by: ReadonlyMap<SuppressionId, EvidenceId>
  readonly stats: {
    readonly active: number
    readonly lifted: number
    readonly reactivated: number
  }
}

export type ApplyInput = {
  readonly suppressions: readonly Suppression[]
  readonly corrections: readonly Correction[]
  readonly groups: readonly LineageGroup[]
  readonly claims: readonly Claim[]
  readonly evidence: readonly Evidence[]
}

/** An appended `unsuppress` correction naming the suppression, dated after it, lifts it. */
export function isLifted(s: Suppression, corrections: readonly Correction[]): boolean {
  return corrections.some(
    (c) =>
      c.reason === 'unsuppress' &&
      c.corrected_value === s.id &&
      Date.parse(c.time) >= Date.parse(s.time),
  )
}

/**
 * Material new evidence: a claim in the suppressed item's lineage group, or a
 * transition on it, whose evidence is dated after the suppression. Returns the
 * earliest such evidence, or null. ▲ A message older than the suppression that
 * only arrives later (a wider export) is not "new" by this definition.
 */
export function materialNewEvidence(
  s: Suppression,
  groups: readonly LineageGroup[],
  claims: readonly Claim[],
  evidence: readonly Evidence[],
): Evidence | null {
  if (s.claim_id === null) return null
  const group = groups.find((g) => g.claim_ids.includes(s.claim_id as ClaimId))
  if (!group) return null
  const evidenceById = new Map(evidence.map((e) => [e.id, e]))
  const claimsById = new Map(claims.map((c) => [c.id, c]))
  const cited = new Set<EvidenceId>()
  for (const id of group.claim_ids)
    for (const e of claimsById.get(id)?.evidence_ids ?? []) cited.add(e)
  for (const t of group.transitions) for (const e of t.evidence_ids) cited.add(e)
  const since = Date.parse(s.time)
  let earliest: Evidence | null = null
  for (const id of cited) {
    const e = evidenceById.get(id)
    if (!e || Date.parse(e.timestamp) <= since) continue
    if (earliest === null || Date.parse(e.timestamp) < Date.parse(earliest.timestamp)) earliest = e
  }
  return earliest
}

export function applyUserSuppressions(input: ApplyInput): UserSuppressionResult {
  const states = new Map<SuppressionId, SuppressionState>()
  const reactivatedBy = new Map<SuppressionId, EvidenceId>()
  const active: Suppression[] = []
  for (const s of input.suppressions) {
    if (isLifted(s, input.corrections)) {
      states.set(s.id, 'lifted')
      continue
    }
    if (s.duration === 'until_new_evidence') {
      const fresh = materialNewEvidence(s, input.groups, input.claims, input.evidence)
      if (fresh) {
        states.set(s.id, 'reactivated')
        reactivatedBy.set(s.id, fresh.id)
        continue
      }
    }
    states.set(s.id, 'active')
    active.push(s)
  }
  const count = (state: SuppressionState): number =>
    [...states.values()].filter((v) => v === state).length
  return {
    records: input.suppressions,
    active,
    states,
    reactivated_by: reactivatedBy,
    stats: { active: count('active'), lifted: count('lifted'), reactivated: count('reactivated') },
  }
}
